'use client';

import { getCollapsedText, getHtmlText } from '@/utils/goods-list-utils';
import { CollapseArrowDownOutlined, CollapseArrowUpOutlined } from '@zeelool/icon';
import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './ExpandableText.module.css';
import { useTranslations } from 'next-intl';

interface ExpandableTextProps {
  text: string;
  maxLines?: number;
  className?: string;
  initialExpanded?: boolean;
  isWeb?: boolean;
  // 文本样式类名，主要用来设置web最大宽度
  textClassName?: string;
  // 是否使用箭头图标而不是文本按钮
  useArrowIcon?: boolean;
  // 箭头图标样式类名
  arrowClassName?: string;
}

export default function ExpandableText({
  text,
  maxLines = 1,
  className = '',
  initialExpanded = false,
  isWeb = false,
  textClassName = '',
  useArrowIcon = false,
  arrowClassName = '',
}: ExpandableTextProps) {
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const [isClient, setIsClient] = useState(false);
  const t = useTranslations('components.expandableText');
  // 切换展开/收起状态
  const toggle = () => setIsExpanded(!isExpanded);
  // 检测文本是否溢出
  const checkOverflow = () => {
    if (!textRef.current) return;

    const element = textRef.current;

    // 创建临时元素测试完整文本
    const testElement = document.createElement('div');
    testElement.style.cssText = `
      position: absolute;
      visibility: hidden;
      width: 'calc(100% - 10px)';
      font-family: ${getComputedStyle(element).fontFamily};
      font-size: ${getComputedStyle(element).fontSize};
      line-height: ${getComputedStyle(element).lineHeight};
      max-width: ${getComputedStyle(element).maxWidth};
      word-wrap: break-word;
    `;
    // 使用收起状态的文本来检测溢出（因为收起状态下文本更短）
    testElement.innerHTML = getCollapsedText(text);
    document.body.appendChild(testElement);

    // 计算单行高度
    const singleLineTest = document.createElement('div');
    singleLineTest.style.cssText = testElement.style.cssText;
    singleLineTest.innerHTML = 'Test';
    document.body.appendChild(singleLineTest);
    const lineHeight = singleLineTest.offsetHeight;
    document.body.removeChild(singleLineTest);

    // 检查是否超出指定行数
    const maxHeight = lineHeight * maxLines;
    const actualHeight = testElement.offsetHeight;

    document.body.removeChild(testElement);

    setIsOverflowing(actualHeight > maxHeight);
  };

  // 客户端标识设置
  useEffect(() => {
    setIsClient(true);
  }, []);

  // 只在客户端执行 checkOverflow
  useEffect(() => {
    if (!isClient) return;

    const timer = setTimeout(checkOverflow, 100);
    window.addEventListener('resize', checkOverflow);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [text, maxLines, isClient]);

  const paddingRight = useMemo(() => {
    if (isOverflowing) {
      // 如果使用箭头图标，需要更小的 padding
      if (useArrowIcon) {
        return isWeb ? '20px' : '20px';
      }
      return isWeb ? '76px' : '15.4667vw';
    }
    return '0';
  }, [isOverflowing, isWeb, useArrowIcon]);

  const height = useMemo(() => {
    if (isExpanded) {
      return 'auto';
    }
    return isWeb ? '22px' : '18px';
  }, [isExpanded, isWeb]);

  // 服务端和客户端初始状态保持一致，都显示收起状态
  if (!isClient) {
    return (
      <div className={className} style={{ height }}>
        <div
          className={`relative ${isWeb ? styles.expandableTextContentWeb : styles.expandableTextContent}`}
        >
          <div
            className="font-ir"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: maxLines,
              WebkitBoxOrient: 'vertical' as const,
              overflow: 'hidden',
            }}
            dangerouslySetInnerHTML={{ __html: getCollapsedText(text) }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={{ height }}>
      {isExpanded ? (
        // 展开状态：按钮自然跟在文本内容后面
        <div
          className={`font-ir ${isWeb ? styles.expandableTextContentWeb : styles.expandableTextContent}`}
        >
          <span
            className={
              isWeb ? styles.expandableTextContentSpanWeb : styles.expandableTextContentSpan
            }
            dangerouslySetInnerHTML={{ __html: getHtmlText(text) }}
          />
          {isOverflowing && (
            <>
              {useArrowIcon ? (
                <CollapseArrowUpOutlined
                  className={`text-[13px] inline-block align-top mt-[2px] ml-[4px] cursor-pointer ${arrowClassName}`}
                  onClick={toggle}
                />
              ) : (
                <span
                  className={`bg-transparent border-none underline cursor-pointer whitespace-nowrap ${isWeb ? 'text-14 leading-[20px] ml-8' : 'text-[12px] leading-[18px] ml-4'}`}
                  onClick={toggle}
                >
                  {t('showLess')}
                </span>
              )}
            </>
          )}
        </div>
      ) : (
        // 收起状态：只保留a标签，移除其他HTML元素，使用 line-clamp 限制行数
        <div
          className={`relative ${isWeb ? styles.expandableTextContentWeb : styles.expandableTextContent}`}
        >
          {useArrowIcon ? (
            <div className="flex items-start gap-x-[4px] flex-nowrap">
              <div
                ref={textRef}
                className={`font-ir flex-1 min-w-0 ${textClassName}`}
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: maxLines,
                  WebkitBoxOrient: 'vertical' as const,
                  overflow: 'hidden',
                }}
                dangerouslySetInnerHTML={{ __html: getCollapsedText(text) }}
              />
              {isOverflowing && (
                <CollapseArrowDownOutlined
                  className={`text-[13px] flex-shrink-0 mt-[2px] cursor-pointer ${arrowClassName}`}
                  onClick={toggle}
                />
              )}
            </div>
          ) : (
            <>
              <div
                ref={textRef}
                className={`font-ir ${textClassName}`}
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: maxLines,
                  WebkitBoxOrient: 'vertical' as const,
                  overflow: 'hidden',
                  paddingRight,
                }}
                dangerouslySetInnerHTML={{ __html: getCollapsedText(text) }}
              />
              {isOverflowing && (
                <div className="absolute bottom-0 right-0">
                  <span
                    className={`bg-transparent border-none underline cursor-pointer whitespace-nowrap ${isWeb ? 'text-14 leading-[20px]' : 'text-[12px] leading-[18px]'}`}
                    onClick={toggle}
                  >
                    {t('readMore')}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
